import { Col, Row, Button } from "antd";
import React from "react";
import DropdownSelect from "../../../components/generic/DropdownSelect";
import { EyeOutlined } from "@ant-design/icons";
import { arrayFilterByControl } from "../../../helper/array_helper";

/**
 * FileParameters — reusable file input section.
 * Shows: Input Location → [Environment (if env location)] → Input from (folder)
 *
 * Props:
 *   title           string     — section label e.g. "File Parameters", "Source File"
 *   inputLocationOptions []    — From previous steps / Environment location
 *   steps           []         — previous step names (used when "From previous steps")
 *   environments    []         — environment list (filtered to those with folders)
 *   folderPaths     []         — folder path options per environment
 *   defaultEnvironment string  — master suite default environment
 *   value           object     — { inputLocation, environment, inputFrom }
 *   onChange        fn(patch)  — called with partial update { inputLocation?, environment?, inputFrom? }
 */
function FileParameters({
  title,
  inputLocationOptions,
  steps = [],
  environments = [],
  folderPaths = [],
  defaultEnvironment,
  value = {},
  onChange,
}) {
  const { inputLocation = "", environment = "", inputFrom = "" } = value;
  const isEnvLocation = inputLocation === "Environment location";
  const isPrevSteps = inputLocation === "From previous steps";
  const envWithFolders = environments.filter((e) => e.folderCount > 0);

  // Derive source options based on selection mode
  const sourceOptions = isPrevSteps
    ? steps
    : isEnvLocation && environment
      ? arrayFilterByControl(folderPaths, "dataSubType", environment)
      : [];

  // Determine label/value keys for source dropdown (steps vs folders)
  const srcKey =
    sourceOptions.length > 0 && sourceOptions[0]?.taskName
      ? "taskName"
      : sourceOptions[0]?.description
        ? "description"
        : "defaultValue";

  const onChangeLocation = (val) => {
    onChange({ inputLocation: val, environment: "", inputFrom: "" });
  };

  const onChangeEnvironment = (val) => {
    onChange({ environment: val, inputFrom: "" });
  };

  const onChangeInputFrom = (val) => {
    onChange({ inputFrom: val });
  };

  return (
    <div style={{ marginBottom: 4 }}>
      <Row style={{ marginBottom: 8 }}>
        <Col span={6}>
          <p className="spec-modal-label">Input Location</p>
        </Col>
        <Col span={18}>
          <DropdownSelect
            placeholder="input file location"
            options={inputLocationOptions}
            valueKey="value"
            labelKey="label"
            onSelect={onChangeLocation}
            value={inputLocation || undefined}
          />
        </Col>
      </Row>

      {isEnvLocation && (
        <Row style={{ marginBottom: 8 }}>
          <Col span={6}>
            <p className="spec-modal-label">Environment</p>
          </Col>
          <Col span={16}>
            <DropdownSelect
              placeholder="select environment"
              labelKey="displayName"
              valueKey="displayName"
              options={envWithFolders}
              onSelect={onChangeEnvironment}
              value={environment || undefined}
            />
          </Col>
          <Col span={2} style={{ display: "flex", justifyContent: "center" }}>
            <Button type="link">
              <EyeOutlined style={{ fontSize: 22, color: "gray" }} />
            </Button>
          </Col>
        </Row>
      )}

      <Row>
        <Col span={6}>
          <p className="spec-modal-label">Input from</p>
        </Col>
        <Col span={18}>
          <DropdownSelect
            placeholder="select input source"
            options={sourceOptions}
            valueKey={srcKey}
            labelKey={srcKey}
            onSelect={onChangeInputFrom}
            value={inputFrom || undefined}
            disabled={!inputLocation}
          />
        </Col>
      </Row>
    </div>
  );
}

export default FileParameters;
